import {Component, OnDestroy, OnInit} from '@angular/core';
import {Location} from '@angular/common';
import {DbResponse} from "../shared/interceptors/db-response";
import {Product} from "../websites/website/product";
import {ActivatedRoute, Router} from "@angular/router";
import {DialogService} from "../../ng2-bootstrap-modal/dialog.service";
import {User} from "../user/user";
import {UserSubscription, UserSubscriptionIds, UserSubscriptionType} from "../user/user-subscription";
import {combineLatest} from 'rxjs';
import {UserService} from "../user/user.service";
import {Observable} from 'rxjs';
import {Store} from '@ngrx/store';
import * as fromStore from '../store';
import {Website} from '../websites/website/website';
import {distinctUntilChanged, filter, first, switchMap, takeUntil} from "rxjs/operators";
import {AuthService} from "../auth/auth.service";
import {GoogleAnalyticsService} from "../shared/services/google-analytics/google-analytics.service";
import {Config} from "../config/config";
import {WorkspaceService} from "../workspace/workspace.service";
import {TypeUtils} from "../shared/utils/type-utils";
import {SignUpFor, TypeFormAnswers} from "./typeFormAnswers";
import {UserMapper} from "../user/user-mapper";
import {Subject} from "rxjs";
import {ExpertsService} from "../services/API/experts.service";
import {CreateWebsiteService} from "../services/local/create-website.service";

@Component({
    selector: 'db-add-website',
    templateUrl: './add-website.component.html',
    styleUrls: ['./add-website.component.scss'],
    standalone: false
})
export class AddWebsiteComponent implements OnInit, OnDestroy {

  public loading: boolean;

  public _user: User;
  public exceededDomains$: Observable<boolean>;
  public upgradeAvailable: boolean;
  public subscription: UserSubscription;
  public premiumPlugin: Product;
  public websites: Website[];
  public userSubscriptionIds = UserSubscriptionIds;
  public isMobile: boolean;
  public signUpForStep: boolean;
  public signUpForAnswers: SignUpFor;
  public isSent: boolean = false;
  public userLocalStorage: any;
  public managerLink: string;
  public typeFormAnswers = TypeFormAnswers;
  public allowCreation: boolean;
  public oneToThreeWebsites: boolean;
  public hostingsLimit: number;

  private formId: string;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _location: Location,
    private _router: Router,
    private _route: ActivatedRoute,
    private _store: Store<fromStore.DbStoreState>,
    private _userService: UserService,
    private _workspaceService: WorkspaceService,
    private _dialogService: DialogService,
    private _authService: AuthService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _expertsService: ExpertsService,
    private _createWebsiteService: CreateWebsiteService,
  ) {}

  ngOnInit(): void {
    this.subscription = this._route.snapshot.data['subscription'];
    this.formId = this.subscription && this.subscription.product_id === '200' ? Config.typeFormExpertId : Config.typeFormOnboardingId;
    this._user = this._route.snapshot.data['user'];
    this.signUpForAnswers = this._user && this._user.signUpFor;
    /*this.signUpForStep = this._user && !this._user.signUpFor;*/
    this.userLocalStorage = this._route.snapshot.data['addWebsiteResolver'] || {};
    this.oneToThreeWebsites = this.signUpForAnswers && this._user.agencyOrFreelancer &&
      [this.typeFormAnswers.FROM_1_TO_3].includes(this.signUpForAnswers.WebsiteNumber.id);
    this.allowCreation = (window.location.href.indexOf("creation") > -1) || (this.subscription && this.subscription.product_id === "169");
    this.managerLink = this._route.snapshot.data['managerPlugin'] && this._route.snapshot.data['managerPlugin'].downloadLink;
    const queryParamsIndex: number = this._router.url.indexOf('?');
    this.hostingsLimit = this._user && this._user.hosting_services && this.subscription && this.subscription.plan ? parseInt(this._user.hosting_services.domain_count.parameter_value, 10) + this.subscription.plan.additionalDomainCount : null;
    if (queryParamsIndex !== -1) {
      this._location.replaceState(this._router.url.substring(0, queryParamsIndex));
    }
    this.addSubscriptions();

    this._store.select(fromStore.getOngoingActivities)
      .pipe(
        filter((loaded: any) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getCreateWebsiteOngoingActivity)
        )),
        takeUntil(this._destroyStream$)
      )
      .subscribe((data: any) => {
        const createActiivity = data[0];

        this.loading = createActiivity && createActiivity.status === 'running' && createActiivity.progress !== 100;

      });
    if (this.websites.length && this.subscription && this.subscription.subscriptionId === UserSubscriptionIds.TRIAL && localStorage.getItem('onboardingFlow') && !this.loading) {
      this._router.navigate(['/websites'], {replaceUrl: true});
    }
  }


  get fromSandbox(): any {
    return localStorage.getItem('dbloginFromSandbox');
  }

  private addSubscriptions(): void {
    const queryParams: any = (<any>this._route.queryParams).value;
    const subscriptionId: number = parseInt(queryParams.subscr_id, 10);
    const fromGoogleSignIn = queryParams.fromGoogleSignIn;

    this._store.select(fromStore.getTenWebProductsLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getTenWebProducts)),
        first()
      )
      .subscribe((products) => {
        this.premiumPlugin = this.subscription.productSlug ? (products.find((product: Product) => product.slug === this.subscription.productSlug)) : null;
      });

    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription),
          this._store.select(fromStore.getUserUpgradableSubscription())),
        ),
        filter(([user, userSubscription]) => !!user && !!userSubscription),
        distinctUntilChanged(([prevUser], [currUser]) => prevUser.signUpFor === currUser.signUpFor),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([user, userSubscription, userUpgradableSubscription]) => {
        if (userSubscription.product_id === '200' && !!user.signUpFor) {
          this._router.navigate(['/websites'], {replaceUrl: true});
        }
        this.upgradeAvailable = !!userUpgradableSubscription;
      });
    this._store
      .select(fromStore.getWebsitesLoaded)
      .pipe(
        filter((loaded: boolean): boolean => !!loaded),
        switchMap(() => this._store.select(fromStore.getWebsites)),
        takeUntil(this._destroyStream$)
      )
      .subscribe((websites: Website[]) => {
        this.websites = websites;
        const lastWebsiteId = this.websites && this.websites.length && this.websites.length - 1;

        if (websites && websites.length && this.subscription && (this.subscription.type === UserSubscriptionType.ONE_PRODUCT || this.subscription.type === UserSubscriptionType.PREMIUM || (this.subscription && (this.subscription.subscriptionId === this.userSubscriptionIds.TRIAL || this.subscription.subscriptionId === this.userSubscriptionIds.TRIAL_OPTIMIZER) && websites[lastWebsiteId] && !websites[lastWebsiteId].domain_hash))) {
          this._router.navigate(['/websites'], {replaceUrl: true});
        }
      });
  }

  public openDialog() {
    this._createWebsiteService.openCreateWebsiteModal(this.subscription && this.subscription.boosterProClient);
  }

  public signOut() {
    if (this._authService.canLogout()) {
      this._authService.logout(null, this._user.id, this.subscription);
    }
  }

  ngOnDestroy(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  public downloadManagerPluginEvent(): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: '10Web Manager Download',
      eventLabel: TypeUtils.isDefined(this._router) && TypeUtils.isDefined(this._router.url) ? this._router.url : 'add-website'
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  private submitTypeForm = (data) => {
    this._userService.sendTypeFormAnswerId(data.response_id, this.formId)
      .pipe(
        filter((response: DbResponse): boolean => !!response),
        first()
      )
      .subscribe((response: DbResponse): void => {
        let signUpFor,
          _dataRole,
          _dataWebsiteCount;

        if (response.success) {
          if (response.data) {
            try {
              signUpFor = JSON.parse(response.data);
            } catch (e) {
              signUpFor = null;
            }
          }
          this.signUpForAnswers = signUpFor && UserMapper.signUpForMapper(signUpFor) || null;
          if (this.signUpForAnswers) {
            if (this.signUpForAnswers.Role && this.signUpForAnswers.Role.id) {
              _dataRole = this.signUpForAnswers.Role.id;
            }
            if (this.signUpForAnswers.WebsiteNumber && this.signUpForAnswers.WebsiteNumber.id) {
              _dataWebsiteCount = this.signUpForAnswers.WebsiteNumber.id;
            }
          }
        }

        if (_dataRole) {
          this.userLocalStorage['signUpFor'] = _dataRole;
        }
        if (this.signUpForAnswers && this.signUpForAnswers.Role && this.signUpForAnswers.WebsiteNumber) {

          this.oneToThreeWebsites = ([this.typeFormAnswers.AGENCY, this.typeFormAnswers.FREELANCE].includes(_dataRole) || this._user.agencyOrFreelancer) &&
            [this.typeFormAnswers.FROM_1_TO_3].includes(_dataWebsiteCount);
        }

        this._store.dispatch(new fromStore.LoadUser());
        this._userService.setToLocal(this.userLocalStorage)
          .pipe(
            filter((_response: DbResponse): boolean => !!_response),
            first(),
            takeUntil(this._destroyStream$)
          )
          .subscribe((_response: DbResponse): void => {
            if (_response.success) {
              this._store.dispatch(new fromStore.LoadUserLocalStorage());
            }
            this.signUpForStep = false;
          });
      });
  }

}
